/**
 * Configuración de gamificación del dominio **MealPlanner**.
 *
 * El motor (`engine.ts`) es agnóstico; aquí se declara QUÉ premia esta app:
 * planificar comidas (XP + racha de planificación por día cubierto), crear
 * recetas (XP) y variedad (recetas DISTINTAS llevadas al plan). Positivo/opcional.
 *
 * `PlayerState` es recomputable en cliente desde las entradas del plan y las
 * recetas ya sincronizadas (local-first): no requiere columnas nuevas. Puro →
 * re-derivable en servidor (antifraude).
 */

import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { type GamificationConfig, applyEvents, initialState } from "./engine";
import type { DayKey, GameEvent, PlayerState } from "./types";

/** Línea de racha: días con al menos una comida planificada (cobertura). */
export const PLANNING_STREAK_LINE = "planning";

export const MEAL_EVENT = {
  planned: "meal.planned",
  recipeCreated: "recipe.created",
  variety: "recipe.variety",
} as const;

export const XP_PER_PLANNED_MEAL = 8;
export const XP_PER_RECIPE = 20;

export const MEALPLAN_GAMIFICATION_CONFIG: GamificationConfig = {
  xp: {
    [MEAL_EVENT.planned]: XP_PER_PLANNED_MEAL,
    [MEAL_EVENT.recipeCreated]: XP_PER_RECIPE,
  },
  streakLines: {
    [MEAL_EVENT.planned]: [PLANNING_STREAK_LINE],
  },
  counters: {
    [MEAL_EVENT.planned]: ["meals"],
    [MEAL_EVENT.recipeCreated]: ["recipes"],
    [MEAL_EVENT.variety]: ["variety"],
  },
  badges: [
    {
      id: "first-plan",
      title: "Primer plato",
      when: counterAtLeast("meals", 1),
    },
    {
      id: "meals-50",
      title: "50 platos",
      when: counterAtLeast("meals", 50),
      bonusXp: 40,
    },
    {
      id: "meals-200",
      title: "Chef planificador",
      when: counterAtLeast("meals", 200),
      bonusXp: 120,
    },
    {
      id: "first-recipe",
      title: "Primera receta",
      when: counterAtLeast("recipes", 1),
      bonusXp: 15,
    },
    {
      id: "recipes-10",
      title: "Recetario",
      when: counterAtLeast("recipes", 10),
      bonusXp: 50,
    },
    {
      id: "variety-10",
      title: "Paladar variado",
      when: counterAtLeast("variety", 10),
      bonusXp: 40,
    },
    {
      id: "planning-streak-3",
      title: "3 días planificados",
      when: streakAtLeast(PLANNING_STREAK_LINE, 3),
      bonusXp: 15,
    },
    {
      id: "planning-streak-7",
      title: "Semana planificada",
      when: streakAtLeast(PLANNING_STREAK_LINE, 7),
      bonusXp: 40,
    },
    {
      id: "planning-streak-30",
      title: "Mes planificado",
      when: streakAtLeast(PLANNING_STREAK_LINE, 30),
      bonusXp: 120,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "weekly-planning",
      title: "Planifica 7 comidas esta semana",
      period: "weekly",
      goals: [{ metric: MEAL_EVENT.planned, target: 7 }],
      bonusXp: 50,
    },
  ],
};

export interface PlannedMealInput {
  /** Día planificado `YYYY-MM-DD`. */
  planDate: string;
  recipeId?: string | null;
}

export interface RecipeInput {
  /** ISO de creación de la receta. */
  createdAt?: string | null;
}

function dayOfIso(iso: string | null | undefined): DayKey | null {
  if (!iso || iso.length < 10) {
    return null;
  }
  return iso.slice(0, 10);
}

/**
 * Deriva los `GameEvent` desde el plan de comidas y las recetas del usuario.
 * Cada entrada del plan puntúa (racha de planificación por día); cada receta
 * nueva puntúa; la primera vez que una receta entra al plan cuenta como
 * "variedad". Ordena por día para determinismo.
 */
export function deriveMealPlanGameEvents(input: {
  plannedMeals: Iterable<PlannedMealInput>;
  recipes: Iterable<RecipeInput>;
}): GameEvent[] {
  const events: GameEvent[] = [];

  for (const r of input.recipes) {
    const day = dayOfIso(r.createdAt);
    if (!day) {
      continue;
    }
    events.push({
      type: MEAL_EVENT.recipeCreated,
      at: Date.parse(`${day}T12:00:00.000Z`),
      day,
    });
  }

  const meals = [...input.plannedMeals]
    .filter((m) => m.planDate && m.planDate.length >= 10)
    .toSorted((a, b) => (a.planDate < b.planDate ? -1 : a.planDate > b.planDate ? 1 : 0));

  const seenRecipes = new Set<string>();
  for (const m of meals) {
    const day = m.planDate.slice(0, 10);
    const at = Date.parse(`${day}T12:00:00.000Z`);
    events.push({ type: MEAL_EVENT.planned, at, day });
    if (m.recipeId && !seenRecipes.has(m.recipeId)) {
      seenRecipes.add(m.recipeId);
      events.push({ type: MEAL_EVENT.variety, at, day });
    }
  }

  events.sort((a, b) => (a.day === b.day ? 0 : (a.day ?? "") < (b.day ?? "") ? -1 : 1));
  return events;
}

/** Recompone el `PlayerState` desde plan + recetas (local-first, puro). */
export function computeMealPlanPlayerState(
  input: {
    plannedMeals: Iterable<PlannedMealInput>;
    recipes: Iterable<RecipeInput>;
  },
  opts: { freezes?: number } = {},
): PlayerState {
  const events = deriveMealPlanGameEvents(input);
  return applyEvents(initialState(opts.freezes ?? 0), events, MEALPLAN_GAMIFICATION_CONFIG).state;
}
